import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { BalancesServerProps } from '../../lib/typegen/balances-server-props';
import style from './style.module.scss';
import { useStore } from '../../lib/hooks/useStore';
import GenericButton from '../button-generic';
import { AppIconType } from '../../lib/typegen/app-icon-type';
import { ViewProps } from '../../lib/typegen/view-props';
import FormInput from '../form-input';
import AccountCard from '../account-card';
import KioAppHelper from '../../lib/helpers/kioApp';
import { formatCurrencyDisplay, wrapCurrencyBidi } from '../../lib/helpers/formatCurrencyDisplay';
import ScrollBar from '../scroll-bar';
import MessageBox from '../message-box';
import { PaymentType } from '../../lib/typegen/payment-type';

type BalanceProps = BalancesServerProps;

enum BalanceView {
	Edit,
	Select,
	Set,
	Confirm,
}

const Balances = observer(
	({
		editView,
		selectView,
		setView,
		confirmView,
		accountCardServerProps,
		nextPageTarget,
		previousPageTarget,
	}: BalanceProps) => {
		const {
			uiStore: {
				setShowModal,
				setTitle,
				handleNavigation,
				setCustomBackAction,
				clearCustomBackAction,
				setShowCart,
				balancesViewState,
				setBalancesViewState,
			},
			accountStore: {
				cartItems,
				confirmAllCartItems,
				focusedCartItem,
				setFocusedAndSelectedCartItemId,
				setCartItemAmountToPay,
				clearFocusedCartItemId,
				confirmCartItem,
				focusedCartItemId,
				areAllCartItemsConfirmed,
			},
			configStore: { getString },
			kioskStore: { getPaymentMethodLimits },
		} = useStore();

		const [currentViewType, setCurrentViewType] = useState<BalanceView>(balancesViewState as BalanceView);
		const [customAmount, setCustomAmount] = useState<string>('');
		const [viewProps, setViewProps] = useState<ViewProps | null>();

		useEffect(() => {
			setBalancesViewState(currentViewType);
		}, [currentViewType, setBalancesViewState]);

		useEffect(() => {
			setShowCart(true);
		}, [setShowCart]);

		useEffect(() => {
			const handleGoBack = () => {
				switch (currentViewType) {
					case BalanceView.Edit:
						handleNavigation(previousPageTarget);
						break;
					case BalanceView.Select:
					case BalanceView.Set:
					case BalanceView.Confirm:
						setCurrentViewType(BalanceView.Edit);
						break;
				}
			};

			setCustomBackAction(handleGoBack);

			return () => clearCustomBackAction();
		}, [clearCustomBackAction, currentViewType, handleNavigation, previousPageTarget, setCustomBackAction]);

		useEffect(() => {
			switch (currentViewType) {
				case BalanceView.Edit:
					setShowModal(false);
					setViewProps(editView);
					setTitle(getString(editView.title.toString()));
					break;
				case BalanceView.Select:
					setShowModal(true);
					setViewProps(selectView);
					setTitle(getString(selectView.title));
					break;
				case BalanceView.Set:
					setShowModal(true);
					setViewProps(setView);
					setTitle(getString(setView.title));
					break;
				case BalanceView.Confirm:
					setShowModal(false);
					setViewProps(confirmView);
					confirmAllCartItems();
					setTitle(getString(confirmView.title));
					break;
			}
		}, [confirmAllCartItems, confirmView, currentViewType, editView, getString, selectView, setShowModal, setTitle, setView]);

		// For views
		const handlePencilClick = (id: string) => {
			setFocusedAndSelectedCartItemId(id);
			setCurrentViewType(BalanceView.Select);
		};

		const handleSelectCloseClick = () => {
			clearFocusedCartItemId();
			setCurrentViewType(BalanceView.Edit);
		};

		const handleSetDoneClick = () => {
			const amountInDollars = parseInt(customAmount) / 100;
			setCartItemAmountToPay(focusedCartItemId!, amountInDollars);
			confirmCartItem(focusedCartItemId!);
			if (areAllCartItemsConfirmed()) setCurrentViewType(BalanceView.Confirm);
			else setCurrentViewType(BalanceView.Edit);
			clearFocusedCartItemId();
		};

		const handleEditNextClick = () => {
			setCurrentViewType(BalanceView.Confirm);
		};

		// For items
		const handlePayOtherClick = (id: string) => {
			setFocusedAndSelectedCartItemId(id);
			setCurrentViewType(BalanceView.Set);
		};

		const handlePayFullClick = (id: string) => {
			setCartItemAmountToPay(id, focusedCartItem!.item.amountDue);
			confirmCartItem(id);
			setCurrentViewType(BalanceView.Edit);
			clearFocusedCartItemId();
		};

		const handlePayPastClick = (id: string) => {
			setCartItemAmountToPay(id, focusedCartItem!.item.amountPastDue);
			confirmCartItem(id);
			setCurrentViewType(BalanceView.Edit);
			clearFocusedCartItemId();
		};

		const handleAmountKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
			e.preventDefault();

			const key = e.key;
			if (key === 'Backspace' || key === 'Delete') {
				const newValue = customAmount.slice(0, -1);
				setCustomAmount(newValue);
				return;
			}

			if (/^[0-9]$/.test(key)) {
				const newValue = customAmount + key;
				setCustomAmount(newValue);
				return;
			}
		};

		const formatDisplayValue = (value: string): string => {
			if (!value || value === '0') return '$0.00';
			const paddedValue = value.padStart(3, '0');
			const cents = paddedValue.slice(-2);
			const dollars = paddedValue.slice(0, -2);
			const cleanDollars = dollars.replace(/^0+/, '') || '0';
			return '$' + cleanDollars + '.' + cents;
		};

		const scrollAccountsUp = () => {
			const accountsEl = document.querySelector(`.${style['edit-view__accounts']}, .${style['confirm-view__accounts']}`);
			if (accountsEl) {
				accountsEl.scrollTop -= 150;
			}
		};

		const scrollAccountsDown = () => {
			const accountsEl = document.querySelector(`.${style['edit-view__accounts']}, .${style['confirm-view__accounts']}`);
			if (accountsEl) {
				accountsEl.scrollTop += 150;
			}
		};
		const renderAccounts = (accounts: 'selected' | 'all', className?: string) => {
			return (accounts === 'all' ? cartItems : [focusedCartItem])
				.filter((i) => i !== undefined)
				.map((item, i) => (
					<AccountCard
						key={i}
						{...accountCardServerProps}
						cartItem={item!}
						className={className}
						showMeta={currentViewType === BalanceView.Edit || currentViewType === BalanceView.Confirm}
						showPayButtonBalances={currentViewType === BalanceView.Select}
						showButton={currentViewType === BalanceView.Edit ? 'edit' : 'none'}
						onEditClick={handlePencilClick}
						onPayFullClick={handlePayFullClick}
						onPayOtherClick={handlePayOtherClick}
						onPayPastClick={handlePayPastClick}
						isHighlighted={false}
					/>
				));
		};

		const renderEditView = () => {
			return (
				<section className={style['edit-view']}>
					<article className={style['edit-view__subtitle']}>{getString(viewProps?.description)}</article>
					<div className={style['accounts-wrapper']}>
						<div className={style['edit-view__accounts']}>{renderAccounts('all')}</div>
						{cartItems.length > 2 && <ScrollBar upAction={scrollAccountsUp} downAction={scrollAccountsDown} />}
					</div>
					<GenericButton
						icon={AppIconType.CircleArrowRight}
						className={style['edit-view__action']}
						innerText={getString('next')}
						isGrowHorizontally={false}
						onClick={handleEditNextClick}
						disabled={cartItems.some((item) => item.amountToPay < 1)}
					/>
				</section>
			);
		};

		const renderSelectView = () => {
			return (
				<section className={style['set-view']}>
					<article className={style['set-view__subtitle']}>{getString(viewProps?.description)}</article>
					{renderAccounts('selected', style['select-view__account'])}
					{limitSummary && (
						<MessageBox type='warning' className={style['set-view__note']}>
							{limitSummary}
						</MessageBox>
					)}
					<GenericButton
						onClick={handleSelectCloseClick}
						isAltColor={true}
						icon={AppIconType.CancelSolid}
						isGrowHorizontally={false}
						className={style['select-view__action']}
						innerText={getString('close')}
					/>
				</section>
			);
		};

		const formatCurrency = (value: number) => wrapCurrencyBidi(formatCurrencyDisplay(value));

		const buildLimitSummary = (): string | null => {
			const methods: { labelKey: string; paymentType: PaymentType }[] = [
				{ labelKey: 'card', paymentType: PaymentType.Card },
				{ labelKey: 'cash', paymentType: PaymentType.Cash },
				{ labelKey: 'check', paymentType: PaymentType.Check },
			];

			// Compose from atomic, individually-translated words. The qualifier words must not be
			// embedded in a composite template — backend-served translations keep those templates in
			// English, so the words would never localize (only the method label key does).
			const minAbbr = getString('payment-limit-min-abbr');
			const maxAbbr = getString('payment-limit-max-abbr');
			const minWord = getString('payment-limit-minimum');
			const maxWord = getString('payment-limit-maximum');

			const lines: string[] = [];
			for (const { labelKey, paymentType } of methods) {
				const { min, max } = getPaymentMethodLimits(paymentType);
				if (min == null && max == null) continue;

				const method = getString(labelKey);
				let line: string;
				if (min != null && max != null) {
					line = `${method}: ${formatCurrency(min)} ${minAbbr} - ${formatCurrency(max)} ${maxAbbr}`;
				} else if (min != null) {
					line = `${method}: ${formatCurrency(min)} ${minWord}`;
				} else {
					line = `${method}: ${formatCurrency(max!)} ${maxWord}`;
				}
				lines.push(line);
			}

			return lines.length > 0 ? lines.join('\n') : null;
		};

		const limitSummary = buildLimitSummary();

		const renderSetView = () => (
			<section className={style['set-view']}>
				<article className={style['set-view__subtitle']}>{getString(viewProps?.description)}</article>
				{renderAccounts('selected')}
				<FormInput
					type={'text'}
					name={'amount'}
					dir='ltr'
					placeholder='$0.00'
					className={style['set-view__input']}
					value={formatDisplayValue(customAmount)}
					onKeyDown={handleAmountKeyDown}
					onFocus={() => {
						setTimeout(() => {
							KioAppHelper('StartKeyboard', '', '', 'number', false, '', 'amount');
						}, 200);
					}}
					readOnly
				/>
				{limitSummary && (
					<MessageBox type='warning' className={style['set-view__note']}>
						{limitSummary}
					</MessageBox>
				)}
				<GenericButton
					onClick={handleSetDoneClick}
					icon={AppIconType.AcceptSolid}
					isGrowHorizontally={false}
					className={style['set-view__action']}
					innerText={getString('done')}
					disabled={!customAmount || parseInt(customAmount) < 100}
					hasGlow={true}
				/>
			</section>
		);
		const renderConfirmView = () => {
			return (
				<section className={style['confirm-view']}>
					<article className={style['confirm-view__subtitle']}>{getString(viewProps?.description)}</article>
					<div className={style['accounts-wrapper']}>
						<div className={style['confirm-view__accounts']}>{renderAccounts('all')}</div>
						{cartItems.length > 2 && <ScrollBar upAction={scrollAccountsUp} downAction={scrollAccountsDown} />}
					</div>
					<GenericButton
						icon={AppIconType.CircleArrowRight}
						className={style['edit-view__action']}
						innerText={getString('next')}
						isGrowHorizontally={false}
						onClick={() => {
							setBalancesViewState(BalanceView.Edit);
							handleNavigation(nextPageTarget);
						}}
					/>
				</section>
			);
		};
		const renderActiveView = () => {
			switch (currentViewType) {
				case BalanceView.Edit:
					return renderEditView();
				case BalanceView.Select:
					return renderSelectView();
				case BalanceView.Set:
					return renderSetView();
				case BalanceView.Confirm:
					return renderConfirmView();
				default:
					return renderEditView();
			}
		};

		return renderActiveView();
	}
);

export default Balances;
